import React from 'react';
import { Shield, Download, RefreshCw } from 'lucide-react';
import { motion } from 'framer-motion';

interface DnssecSettings {
  enabled: boolean;
  algorithm: string;
  keyLength: number;
  autoRotation: boolean;
  rotationPeriod: number;
}

interface Zone {
  id: string;
  name: string;
  dnssecEnabled: boolean;
}

interface Props {
  dnssecSettings: DnssecSettings;
  setDnssecSettings: (settings: DnssecSettings) => void;
  zones: Zone[];
  loading: boolean;
  onDnssecToggle: (zoneId: string) => void;
  onSaveSettings: () => void;
}

const DnssecTab: React.FC<Props> = ({
  dnssecSettings,
  setDnssecSettings,
  zones,
  loading,
  onDnssecToggle,
  onSaveSettings,
}) => {
  return (
    <div className="space-y-6">
      {/* DNSSEC Overview */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-6">
        <div className="flex items-start">
          <Shield className="w-6 h-6 text-blue-600 dark:text-blue-400 mt-1 mr-3 flex-shrink-0" />
          <div>
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
              DNS Security Extensions (DNSSEC)
            </h3>
            <p className="text-blue-700 dark:text-blue-300 text-sm">
              DNSSEC provides cryptographic authentication for DNS responses, protecting
              against DNS spoofing and cache poisoning attacks.
            </p>
          </div>
        </div>
      </div>

      {/* Global DNSSEC Settings */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-4">
          Global DNSSEC Settings
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Default Algorithm
            </label>
            <select
              value={dnssecSettings.algorithm}
              onChange={(e) =>
                setDnssecSettings({ ...dnssecSettings, algorithm: e.target.value })
              }
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ECDSA-P256-SHA256">ECDSA P-256 SHA256</option>
              <option value="RSA-SHA256">RSA SHA256</option>
              <option value="ECDSA-P384-SHA384">ECDSA P-384 SHA384</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Key Length
            </label>
            <select
              value={dnssecSettings.keyLength}
              onChange={(e) =>
                setDnssecSettings({ ...dnssecSettings, keyLength: parseInt(e.target.value) })
              }
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={256}>256 bits</option>
              <option value={384}>384 bits</option>
              <option value={2048}>2048 bits (RSA only)</option>
            </select>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-sm font-medium text-slate-900 dark:text-slate-100">
                Automatic Key Rotation
              </h4>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Automatically rotate DNSSEC keys
              </p>
            </div>
            <input
              type="checkbox"
              checked={dnssecSettings.autoRotation}
              onChange={(e) =>
                setDnssecSettings({ ...dnssecSettings, autoRotation: e.target.checked })
              }
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
              Rotation Period (days)
            </label>
            <input
              type="number"
              value={dnssecSettings.rotationPeriod}
              onChange={(e) =>
                setDnssecSettings({
                  ...dnssecSettings,
                  rotationPeriod: parseInt(e.target.value),
                })
              }
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={onSaveSettings}
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            Save Settings
          </button>
        </div>
      </div>

      {/* Zone DNSSEC Status */}
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100">
            Zone DNSSEC Status
          </h3>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            disabled={loading}
          >
            Enable All Zones
          </motion.button>
        </div>

        <div className="space-y-3">
          {zones.map((zone) => (
            <div
              key={zone.id}
              className="flex items-center justify-between p-4 border border-slate-200 dark:border-slate-600 rounded-lg"
            >
              <div className="flex items-center space-x-3">
                <div
                  className={`w-3 h-3 rounded-full ${
                    zone.dnssecEnabled ? 'bg-green-500' : 'bg-slate-300'
                  }`}
                />
                <span className="font-medium text-slate-900 dark:text-slate-100">
                  {zone.name}
                </span>
                {zone.dnssecEnabled && (
                  <span className="px-2 py-1 text-xs bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 rounded-full">
                    DNSSEC Enabled
                  </span>
                )}
              </div>

              <div className="flex items-center space-x-3">
                {zone.dnssecEnabled && (
                  <button
                    onClick={() => {
                      /* Show DS record */
                    }}
                    className="px-3 py-1 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                  >
                    <Download className="w-3 h-3 inline mr-1" />
                    DS Record
                  </button>
                )}

                <button
                  onClick={() => onDnssecToggle(zone.id)}
                  disabled={loading}
                  className={`px-3 py-1 text-xs rounded-lg transition-colors ${
                    zone.dnssecEnabled
                      ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200 hover:bg-red-200 dark:hover:bg-red-800'
                      : 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 hover:bg-green-200 dark:hover:bg-green-800'
                  }`}
                >
                  {loading ? (
                    <RefreshCw className="w-3 h-3 animate-spin" />
                  ) : zone.dnssecEnabled ? (
                    'Disable'
                  ) : (
                    'Enable'
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DnssecTab;